import React, { useEffect, useRef, useState } from 'react';


import { Stack } from 'ui/stack';

import Modal from 'components/partials/modal/modal';
import Button from 'components/partials/button/button';
import Input from 'components/partials/input/input';
import { TextArea } from 'ui/text-area';

import {
  AID_MODEL_DESCRIPTION_MAX_LENGTH,
  AID_MODEL_NAME_MAX_LENGTH,
} from '../../aid-studio-details.constants';

export interface CopyModelValues {
  name: string;
  description: string;
}

interface CopyModelModalProps {
  headerText: string;
  onConfirm: (values: CopyModelValues) => Promise<void>;
  onClose: () => void;
}

const CopyModelModal = ({
  headerText,
  onConfirm,
  onClose,
}: CopyModelModalProps): React.JSX.Element => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const nameInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    nameInputRef.current?.focus();
  }, []);

  const trimmedName = name.trim();
  const isDisabled = !trimmedName || isSubmitting;

  const handleConfirm = async (): Promise<void> => {
    if (isDisabled) return;

    setIsSubmitting(true);
    try {
      await onConfirm({
        name: trimmedName,
        description: description.trim(),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      headerText={headerText}
      headerClassName="!text-2xl font-bold"
      onCloseCallback={onClose}
      skipUnmountOnClose
      showCloseButton
      primaryAction={{
        onClick: handleConfirm,
        disabled: isDisabled,
        loading: isSubmitting,
      }}
      data-testid="copy-model-modal"
      modalClassName="!w-[640px] px-5 pt-4 pb-5"
      footer={
        <Stack
          direction="row"
          sx={{ justifyContent: 'flex-start', alignItems: 'center', gap: 2 }}
        >
          <Button
            type="button"
            variant="brand-filled"
            size="md"
            onClick={handleConfirm}
            disabled={isDisabled}
            loading={isSubmitting}
            data-testid="confirm-copy-model-button"
          >
            Save
          </Button>
          <Button
            type="button"
            variant="brand-outlined"
            size="md"
            onClick={onClose}
            data-testid="cancel-copy-model-button"
          >
            Cancel
          </Button>
        </Stack>
      }
    >
      <Stack sx={{ mt: 2, gap: 2 }}>
        <Input
          ref={nameInputRef}
          label="New model name"
          labelFor="copy-model-name"
          labelInTop
          required
          value={name}
          maxLength={AID_MODEL_NAME_MAX_LENGTH}
          onChange={(event) => setName(event.target.value)}
          showCharCount={false}
          placeholder=""
          customClass="mt-0 text-md"
          inputCustomClass="h-10"
          data-testid="copy-model-name-input"
        />

        <TextArea
          label="Description"
          id="copy-model-description"
          value={description}
          maxLength={AID_MODEL_DESCRIPTION_MAX_LENGTH}
          rows={4}
          showCharCount={false}
          onChange={setDescription}
          placeholder=""
          data-testid="copy-model-description-textarea"
        />
      </Stack>
    </Modal>
  );
};

export default CopyModelModal;
